import { useFormatTimestamp } from "@/shared/components/RobotoMarkdown/timeFormat";
import {
  ConfirmationCard,
  ConfirmationEmpty,
  ConfirmationField,
  ConfirmationFields,
  ConfirmationPanel,
} from "@/shared/foundation/primitives";

import type { ConfirmationContentProps } from "./ClientTools";
import styles from "./CreateEventsConfirmation.module.css";

interface EventAssociation {
  dataset_id?: string;
  file_id?: string;
  topic_id?: string;
  message_path_id?: string;
}

interface EventTopicParams {
  topic_id?: string;
  field_ids?: string[];
}

interface EventParams {
  name?: string;
  description?: string;
  start_time?: number;
  end_time?: number;
  associations?: EventAssociation[];
  session_id?: string;
  topics?: EventTopicParams[];
  tags?: string[];
  display_options?: { color?: string };
}

/**
 * The subset of the input of `client_create_events` (dataset events) and
 * `client_create_session_events` this preview reads.
 *
 * Declared here rather than imported from those tools' modules, which import
 * this component — a shared definition there would be an import cycle
 * (`import/no-cycle` is an error). Both tools' inputs are assignable to this,
 * which is what lets `createClientTool` accept this component for them and,
 * in doing so, check they agree.
 */
interface CreateEventsPreviewParams {
  events?: EventParams[];
}

/**
 * Summarize associations into a short display string.
 */
function formatAssociations(associations: EventAssociation[]): string {
  return associations
    .map((a) => {
      if (a.dataset_id) {
        return `Dataset: ${a.dataset_id}`;
      }
      if (a.file_id) {
        return `File: ${a.file_id}`;
      }
      if (a.topic_id) {
        return `Topic: ${a.topic_id}`;
      }
      if (a.message_path_id) {
        return `Message Path: ${a.message_path_id}`;
      }
      return "Unknown";
    })
    .join(", ");
}

/**
 * Summarize a session event's topics: each topic, with its field IDs in
 * parentheses when it names any.
 */
function formatTopics(topics: EventTopicParams[]): string {
  return topics
    .map((topic) => {
      const topicId = topic.topic_id ?? "Unknown";
      return topic.field_ids && topic.field_ids.length > 0
        ? `${topicId} (${topic.field_ids.join(", ")})`
        : topicId;
    })
    .join(", ");
}

/**
 * Read-only preview of the events the LLM wants to create.
 * Rendered inline on the ToolApprovalCard and in its details modal.
 */
export function CreateEventsConfirmation({
  params,
}: ConfirmationContentProps<CreateEventsPreviewParams>) {
  // The times the agent proposes read the way the rest of the thread does: in
  // the AI chat's time format, at the decimal places the reader picked. A
  // value no `Date` can hold (NaN, or beyond ±8.64e21 ns) shows as the raw
  // number, since formatting it would throw and take the preview down.
  const formatTimestamp = useFormatTimestamp();
  const formatNanoseconds = (ns: number): string =>
    Number.isNaN(new Date(ns / 1_000_000).getTime())
      ? String(ns)
      : formatTimestamp(BigInt(Math.round(ns)));
  const rawEvents: unknown = params.events;
  const events = Array.isArray(rawEvents) ? (rawEvents as EventParams[]) : [];

  if (events.length === 0) {
    return (
      <ConfirmationPanel label="Create events preview">
        <ConfirmationEmpty>No events to create.</ConfirmationEmpty>
      </ConfirmationPanel>
    );
  }

  return (
    <ConfirmationPanel label="Create events preview">
      {events.map((event, index) => (
        <ConfirmationCard
          key={index}
          title={
            <span className={styles.eventName}>
              {event.display_options?.color && (
                <span
                  className={styles.colorSwatch}
                  style={{ backgroundColor: event.display_options.color }}
                />
              )}
              {event.name ?? "Unnamed Event"}
            </span>
          }
        >
          <ConfirmationFields>
            {event.description && (
              <ConfirmationField label="Description">
                {event.description}
              </ConfirmationField>
            )}

            {event.start_time !== undefined && event.end_time !== undefined && (
              <ConfirmationField label="Time">
                {formatNanoseconds(event.start_time)}
                {event.start_time !== event.end_time &&
                  ` → ${formatNanoseconds(event.end_time)}`}
              </ConfirmationField>
            )}

            {event.associations && event.associations.length > 0 && (
              <ConfirmationField label="Associations">
                {formatAssociations(event.associations)}
              </ConfirmationField>
            )}

            {event.session_id && (
              <ConfirmationField label="Session">
                {event.session_id}
              </ConfirmationField>
            )}

            {event.topics && event.topics.length > 0 && (
              <ConfirmationField label="Topics">
                {formatTopics(event.topics)}
              </ConfirmationField>
            )}

            {event.tags && event.tags.length > 0 && (
              <ConfirmationField label="Tags">
                {event.tags.join(", ")}
              </ConfirmationField>
            )}
          </ConfirmationFields>
        </ConfirmationCard>
      ))}
    </ConfirmationPanel>
  );
}
